import React, { ComponentType, lazy, LazyExoticComponent } from 'react';

const CHUNK_RELOAD_KEY = 'chatyme-chunk-reload-attempted';

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\d]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return '';
};

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const message = getErrorMessage(error);
  const name = error instanceof Error ? error.name : '';

  if (name === 'ChunkLoadError') {
    return true;
  }

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

const isChunkScriptSyntaxError = (message: string, filename?: string): boolean => {
  if (!/Unexpected token '<'/i.test(message)) {
    return false;
  }

  if (!filename) {
    return true;
  }

  return /\.chunk\.(js|mjs)(\?|$)/i.test(filename) || /\/static\/js\//i.test(filename);
};

export const clearChunkReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Ignore storage errors (private browsing, etc.)
  }
};

export const reloadForStaleChunks = (): boolean => {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1') {
      // Already tried a cache-bust reload this tab session; let the UI fallback show.
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch {
    // If storage is unavailable, still attempt one reload.
  }

  // Soft reload can keep a year-cached index.html / main.*.js from before no-cache headers.
  // Cache-bust the URL once so the browser must re-fetch the entry point.
  const url = new URL(window.location.href);
  url.searchParams.set('_ch', String(Date.now()));
  window.location.replace(url.toString());
  return true;
};

export const initChunkLoadRecovery = (): void => {
  // Clear one-shot reload flag after a successful boot (and strip cache-bust query).
  clearChunkReloadFlag();
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_ch')) {
      url.searchParams.delete('_ch');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  } catch {
    // Ignore URL cleanup errors.
  }

  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement | null;
    if (
      target &&
      target.tagName === 'SCRIPT' &&
      'src' in target &&
      typeof (target as HTMLScriptElement).src === 'string' &&
      /\/static\/js\//i.test((target as HTMLScriptElement).src)
    ) {
      reloadForStaleChunks();
      return;
    }

    if (isChunkScriptSyntaxError(event.message, event.filename)) {
      reloadForStaleChunks();
      return;
    }

    if (isChunkLoadError(event.error ?? event.message)) {
      reloadForStaleChunks();
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadForStaleChunks();
    }
  });
};

export const lazyWithChunkReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> => {
  return lazy(async () => {
    try {
      const module = await factory();
      clearChunkReloadFlag();
      return module;
    } catch (error) {
      if (isChunkLoadError(error)) {
        reloadForStaleChunks();
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
};

interface ChunkLoadErrorBoundaryProps {
  children: React.ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  hasError: boolean;
}

export class ChunkLoadErrorBoundary extends React.Component<
  ChunkLoadErrorBoundaryProps,
  ChunkLoadErrorBoundaryState
> {
  state: ChunkLoadErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ChunkLoadErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    if (isChunkLoadError(error) && reloadForStaleChunks()) {
      return;
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="chatyme-chunk-error-fallback" role="alert">
        <p>A new version of the app is available.</p>
        <button
          type="button"
          onClick={() => {
            clearChunkReloadFlag();
            const url = new URL(window.location.href);
            url.searchParams.set('_ch', String(Date.now()));
            window.location.replace(url.toString());
          }}
        >
          Refresh page
        </button>
      </div>
    );
  }
}
